import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import AngelTaxForm from './angelTaxForm';
import { CALCULATION_NOTES } from './angelTaxNotes';
import AngelTaxResultView from './angelTaxResultView';
import NewsPicksBanner, {
  type NewsPicksBannerProps,
} from '../common/newsPicksBanner';
import { type AngelTaxResult } from '../../models/questions/angelTaxCalculator';

interface Props {
  taxReturnImage: string;
  taxWithholdingImage: string;
  // 「案件一覧を見る」の遷移先（案件一覧の「全て」）
  projectsPath: string;
  // 配信期間外は null
  newsPicksBanner: NewsPicksBannerProps | null;
}

export default function AngelTaxView({
  taxReturnImage,
  taxWithholdingImage,
  projectsPath,
  newsPicksBanner,
}: Props) {
  const [result, setResult] = useState<AngelTaxResult | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // 結果セクションの上端がヘッダーの直下に来る位置まで送る。ヘッダーは sticky で React の外にあるので高さは実測する
  const scrollToResult = () => {
    if (resultRef.current == null) return;
    const headerHeight =
      document.getElementById('g-header')?.getBoundingClientRect().height ?? 0;
    window.scrollTo({
      top:
        resultRef.current.getBoundingClientRect().top +
        window.scrollY -
        headerHeight,
      behavior: 'smooth',
    });
  };

  const handleCalculate = (calculated: AngelTaxResult) => {
    // 結果を描画してからその位置へ送るため、state の反映を同期的に済ませる
    flushSync(() => {
      setResult(calculated);
    });
    scrollToResult();
  };

  return (
    <div className="ui-root tw:font-hiragino">
      {/* 上下の余白はこのページの余白を全部ここで持つ（レガシー SCSS 側は reboot の既定を 0 にしているだけ）。
          左右は SP で Figma の 20px。レイアウトの .container が持つ 15px を負のマージンで打ち消してから取る */}
      <div className="tw:-mx-3.75 tw:flex tw:flex-col tw:items-center tw:gap-11 tw:px-5 tw:pt-10 tw:pb-10 tw:md:mx-0 tw:md:gap-12 tw:md:px-0 tw:md:pt-20 tw:md:pb-15">
        {/* NewsPicks 番組バナー（配信期間中のみ）。タイトルとの間隔は親の gap に任せる */}
        {newsPicksBanner != null && (
          <NewsPicksBanner banner={newsPicksBanner} />
        )}
        <div className="tw:flex tw:w-full tw:max-w-150 tw:flex-col tw:items-center tw:gap-4">
          <h1 className="tw:m-0 tw:text-center tw:text-xl tw:leading-normal tw:font-semibold tw:tracking-text tw:md:text-4xl">
            エンジェル税制シミュレーター
          </h1>
          <p className="tw:m-0 tw:text-center tw:font-noto tw:text-(length:--angel-tax-text-note) tw:leading-normal tw:md:text-base">
            エンジェル税制には、優遇措置A、優遇措置B、プレシード・シード特例があり、
            <br className="tw:hidden tw:md:inline" />
            所得控除や株式売却益からの控除などそれぞれ受けられる税制優遇が異なります。
          </p>
        </div>
        <div className="tw:flex tw:w-full tw:max-w-190 tw:flex-col tw:items-center tw:gap-11 tw:md:gap-12">
          <AngelTaxForm
            taxReturnImage={taxReturnImage}
            taxWithholdingImage={taxWithholdingImage}
            calculated={result != null}
            onCalculate={handleCalculate}
          />
          <div className="tw:flex tw:w-full tw:flex-col tw:gap-2 tw:text-xs tw:tracking-text tw:text-muted-foreground tw:md:gap-1">
            <p className="tw:m-0 tw:font-noto tw:leading-none tw:font-bold">
              所得税の計算について
            </p>
            <p className="tw:m-0 tw:leading-normal">
              {`※${CALCULATION_NOTES.join('')}`}
            </p>
          </div>
        </div>
      </div>

      <div ref={resultRef}>
        <AngelTaxResultView result={result} projectsPath={projectsPath} />
      </div>

      <div className="angel-tax__cta">
        <div className="angel-tax__cta__inner">
          <div className="angel-tax__cta__text">
            最短３分！WEBで完結！
            <br />
            登録後、新しい案件情報をお届けします。
          </div>
          <a href="/users/sign_up" className="angel-tax__cta__btn">
            無料で投資家登録
          </a>
        </div>
      </div>
    </div>
  );
}
